import {
    deleteConversation,
    fetchConversations,
    upsertConversation,
} from '@/utils/api/chat.api';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { ChatSessionMeta } from './types';
import type { UIMessage } from '@ai-sdk/react';

const SESSION_INDEX_KEY = 'chatSessions';

export function useSessionIndex() {
    return useQuery(
        SESSION_INDEX_KEY,
        async () => {
            const res = await fetchConversations();
            return (res?.conversations ?? []).map(
                (c: {
                    id: string;
                    label: string;
                    createdAt: string;
                    updatedAt: string;
                }) => ({
                    id: c.id,
                    label: c.label,
                    createdAt: c.createdAt
                        ? new Date(c.createdAt).getTime()
                        : Date.now(),
                    updatedAt: c.updatedAt
                        ? new Date(c.updatedAt).getTime()
                        : Date.now(),
                }),
            ) as ChatSessionMeta[];
        },
        { staleTime: 30_000 },
    );
}

/** Result type for save mutation — includes server updatedAt and optional conflict data. */
export interface SaveResult {
    updatedAt: string | null;
    conflict?: {
        id: string;
        label: string;
        messages: unknown[];
        updatedAt: string | null;
    };
}

export function useSaveSession() {
    const queryClient = useQueryClient();
    return useMutation<
        SaveResult,
        unknown,
        {
            id: string;
            messages: UIMessage[];
            meta: ChatSessionMeta;
            updatedAt?: string | null;
        }
    >(
        async ({ id, messages, meta, updatedAt }) => {
            const res = await upsertConversation({
                id,
                label: meta.label,
                messages: messages as unknown[],
                updatedAt: updatedAt ?? undefined,
            });

            // The endpointWrapper may return the parsed JSON or throw.
            // A 409 from our backend returns {error, id, label, messages, updatedAt}.
            if (res?.error && res.messages) {
                return {
                    updatedAt: null,
                    conflict: {
                        id: res.id,
                        label: res.label,
                        messages: res.messages,
                        updatedAt: res.updatedAt ?? null,
                    },
                };
            }

            return { updatedAt: res?.updatedAt ?? null };
        },
        {
            onMutate: async ({ meta }) => {
                // Cancel any outgoing refetches so they don't overwrite our optimistic update
                await queryClient.cancelQueries(SESSION_INDEX_KEY);

                const previous =
                    queryClient.getQueryData<ChatSessionMeta[]>(
                        SESSION_INDEX_KEY,
                    );

                queryClient.setQueryData<ChatSessionMeta[]>(
                    SESSION_INDEX_KEY,
                    (old = []) => {
                        const idx = old.findIndex((s) => s.id === meta.id);
                        if (idx >= 0) {
                            const updated = [...old];
                            updated[idx] = meta;
                            return updated;
                        }
                        return [meta, ...old];
                    },
                );

                return { previous };
            },
            onError: (_err, _vars, context) => {
                // Roll back optimistic update on error
                if (
                    (context as { previous?: ChatSessionMeta[] } | undefined)
                        ?.previous
                ) {
                    queryClient.setQueryData(
                        SESSION_INDEX_KEY,
                        (
                            context as
                                { previous?: ChatSessionMeta[] } | undefined
                        ).previous,
                    );
                }
            },
            // No onSuccess invalidation — the optimistic update is sufficient
        },
    );
}

export function useDeleteSession() {
    const queryClient = useQueryClient();
    return useMutation(
        async (sessionId: string) => {
            await deleteConversation(sessionId);
        },
        {
            onMutate: async (sessionId) => {
                await queryClient.cancelQueries(SESSION_INDEX_KEY);

                const previous =
                    queryClient.getQueryData<ChatSessionMeta[]>(
                        SESSION_INDEX_KEY,
                    );

                queryClient.setQueryData<ChatSessionMeta[]>(
                    SESSION_INDEX_KEY,
                    (old = []) => old.filter((s) => s.id !== sessionId),
                );

                return { previous };
            },
            onError: (_err, _vars, context) => {
                if (
                    (context as { previous?: ChatSessionMeta[] } | undefined)
                        ?.previous
                ) {
                    queryClient.setQueryData(
                        SESSION_INDEX_KEY,
                        (
                            context as
                                { previous?: ChatSessionMeta[] } | undefined
                        ).previous,
                    );
                }
            },
        },
    );
}
